/**
 * features/dashboard/hooks — hooks de leitura do dashboard (DD-004, Req 10).
 *
 * Consomem `reportsRepo` e `opportunitiesRepo` via `useTenantQuery` (cache
 * segmentado por tenant/BU). A fonte (api vs. mock) é resolvida dentro dos
 * próprios repos via `data/source.ts`.
 *
 * Derivação de KPIs:
 *   - pipelineTotal   → soma de FunnelReportRow.totalValue (estágios "open")
 *   - weighted        → totalByCurrency do ForecastReport (BRL, primeira moeda)
 *   - wonQuarter      → soma de RankingRow.won (estágios won no período)
 *   - quotaQuarter    → sem fonte backend nesta onda → null (exibe "—")
 *   - winRate         → sem fonte backend nesta onda → null (exibe "—")
 *   - avgCycleDays    → sem fonte backend nesta onda → null (exibe "—")
 *   - openCount       → totalCount de listOpportunities (stageCategory = open)
 *
 * Filtro de período:
 *   "m" = mês corrente  → {from: YYYY-MM-01, to: YYYY-MM-last}
 *   "q" = trimestre     → {from: YYYY-QM-01, to: YYYY-QM-last}
 *   "y" = ano corrente  → {from: YYYY-01-01, to: YYYY-12-31}
 */

import { useMemo } from "react";
import { useTenantQuery } from "../../app/providers/QueryProvider.js";
import { fetchReports, type ReportRange } from "../../data/repos/reportsRepo.js";
import { listOpportunities } from "../../data/repos/opportunitiesRepo.js";
import { listActivities } from "../../data/repos/activitiesRepo.js";
import { listInbox } from "../../data/repos/inboxRepo.js";
import type { Money } from "../../shared/money/money.js";
import type { DashboardKpis, FunnelItem } from "./types.js";
import type { FunnelReportRow, RankingRow, ReportsData } from "../reports/types.js";
import type { ActivitiesPage } from "../../data/repos/activitiesRepo.js";
import type { InboxItemData } from "../inbox/types.js";

// ---------------------------------------------------------------------------
// Mapeamento período → {from, to}
// ---------------------------------------------------------------------------

export type DashboardPeriod = "m" | "q" | "y";

/**
 * Deriva {from, to} a partir do período selecionado (relativo ao dia corrente).
 *
 * "m" → mês corrente   (ex.: 2026-06-01 .. 2026-06-30)
 * "q" → trimestre Q    (ex.: 2026-04-01 .. 2026-06-30)
 * "y" → ano corrente   (ex.: 2026-01-01 .. 2026-12-31)
 */
export function derivePeriodRange(period: DashboardPeriod): ReportRange {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-based

  if (period === "m") {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return {
      from: formatDate(firstDay),
      to:   formatDate(lastDay),
    };
  }

  if (period === "q") {
    const quarterStart = Math.floor(month / 3) * 3;
    const firstDay = new Date(year, quarterStart, 1);
    const lastDay = new Date(year, quarterStart + 3, 0);
    return {
      from: formatDate(firstDay),
      to:   formatDate(lastDay),
    };
  }

  // "y"
  return {
    from: `${year}-01-01`,
    to:   `${year}-12-31`,
  };
}

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ---------------------------------------------------------------------------
// Derivação de DashboardKpis a partir dos dados reais
// ---------------------------------------------------------------------------

/**
 * Deriva os KPIs a partir de funnel/forecast/ranking + openCount.
 *
 * KPIs deriváveis:
 *   pipelineTotal → soma de FunnelReportRow.totalValue (todos os estágios, BRL)
 *   weighted      → ForecastReport.totalByCurrency["BRL"] (primeiro disponível)
 *   wonQuarter    → soma de RankingRow.won (BRL)
 *   openCount     → fornecido separadamente (do listOpportunities)
 *
 * KPIs sem fonte nesta onda (exibem "—"):
 *   quotaQuarter  → null
 *   winRate       → null
 *   avgCycleDays  → null
 */
function deriveKpisFromReports(
  reports: ReportsData,
  openCount: number,
): DashboardKpis {
  const { funnel, forecast, ranking } = reports;

  // Pipeline total: soma todos os estágios do funil (BRL)
  const pipelineTotalCents = funnel.reduce(
    (sum: number, row: FunnelReportRow) =>
      row.totalValue.currency === "BRL"
        ? sum + row.totalValue.amountCents
        : sum,
    0,
  );
  const pipelineTotal: Money = { amountCents: pipelineTotalCents, currency: "BRL" };

  // Forecast ponderado: total BRL do forecast (pode ser multimoeda — usamos BRL)
  const weightedCents = forecast?.totalByCurrency["BRL"] ?? 0;
  const weighted: Money = { amountCents: weightedCents, currency: "BRL" };

  // Ganho no período: soma wonValueCents do ranking (BRL)
  const wonCents = ranking.reduce(
    (sum: number, row: RankingRow) =>
      row.won.currency === "BRL" ? sum + row.won.amountCents : sum,
    0,
  );
  const wonQuarter: Money = { amountCents: wonCents, currency: "BRL" };

  return {
    pipelineTotal,
    weighted,
    wonQuarter,
    // quotaQuarter, winRate e avgCycleDays sem fonte nesta onda → null
    quotaQuarter: null,
    winRate: null,
    avgCycleDays: null,
    openCount,
  };
}

// ---------------------------------------------------------------------------
// Prioridade de ordenação de estágio
// ---------------------------------------------------------------------------

/**
 * Prioridade de ordenação por nome de estágio (lower = aparece primeiro).
 *
 * Estágios "open" recebem prioridade 50 (aparece pela ordem de chegada).
 * Estágios terminais conhecidos recebem prioridades maiores:
 *   won / ganho   → 90  (antes de lost, depois de open)
 *   lost / perdido → 100 (ao final)
 *
 * Heurística: como `FunnelReportRow` não carrega a categoria canônica do
 * backend (category: "open" | "won" | "lost"), usamos o nome do estágio
 * como chave de detecção. Funciona para os nomes padrão do Azim CRM.
 */
const STAGE_TERMINAL_PRIORITY: Readonly<Record<string, number>> = {
  won:      90,
  ganho:    90,
  lost:    100,
  perdido: 100,
};

function stageSortPriority(stageName: string): number {
  return STAGE_TERMINAL_PRIORITY[stageName.toLowerCase()] ?? 50;
}

/**
 * Agrupa linhas do funil por nome de estágio (case-insensitive), somando
 * `count` e `totalValue.amountCents` das linhas com mesmo nome.
 *
 * Garante estágios únicos e ordenação estável:
 *   - estágios open (prioridade 50) → pelo índice de primeira aparição
 *   - estágios won  (prioridade 90) → após os open
 *   - estágios lost (prioridade 100) → ao final
 *
 * Resolve a duplicação causada pelo backend retornar estágios de múltiplas
 * BUs com o mesmo nome (ex.: "Proposta" e "Perdido" de BUs diferentes).
 */
export function mergeFunnelRows(rows: readonly FunnelReportRow[]): FunnelItem[] {
  type Entry = {
    stage: string;
    count: number;
    amountCents: number;
    currency: Money["currency"];
    firstIndex: number;
  };

  const seen = new Map<string, Entry>();

  rows.forEach((r, idx) => {
    const key = r.stage.toLowerCase();
    const existing = seen.get(key);
    if (existing) {
      existing.count += r.count;
      existing.amountCents += r.totalValue.amountCents;
    } else {
      seen.set(key, {
        stage: r.stage,
        count: r.count,
        amountCents: r.totalValue.amountCents,
        currency: r.totalValue.currency,
        firstIndex: idx,
      });
    }
  });

  return [...seen.values()]
    .sort((a, b) => {
      const pa = stageSortPriority(a.stage);
      const pb = stageSortPriority(b.stage);
      if (pa !== pb) return pa - pb;
      return a.firstIndex - b.firstIndex;
    })
    .map(({ stage, count, amountCents, currency }) => ({
      stage,
      count,
      value: { amountCents, currency },
    }));
}

// ---------------------------------------------------------------------------
// Hook: useDashboardData — dados centrais do dashboard
// ---------------------------------------------------------------------------

export interface UseDashboardDataResult {
  kpis: DashboardKpis | null;
  funnel: FunnelItem[];
  ranking: readonly RankingRow[];
  activities: ActivitiesPage | null;
  inbox: InboxItemData[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Busca todos os dados necessários para o dashboard (reports + opportunities count
 * + activities + inbox). Refaz o fetch ao mudar buId ou period.
 *
 * A `queryKey` inclui `period` → mudar o filtro invalida o cache e re-busca.
 */
export function useDashboardData(
  buId?: string | null,
  period: DashboardPeriod = "q",
): UseDashboardDataResult {
  const range = useMemo(() => derivePeriodRange(period), [period]);

  // Reports: funnel + forecast + ranking
  const reportsQuery = useTenantQuery<ReportsData>(
    "dashboard-reports",
    () => fetchReports(range),
    { params: { buId: buId ?? null, period }, staleTime: 30_000 },
  );

  // Open opportunity count (listOpportunities page 1, pageSize 1 → pega totalCount)
  const oppsQuery = useTenantQuery<number>(
    "dashboard-opps-count",
    async () => {
      const page = await listOpportunities({ page: 1, pageSize: 1 });
      return page.totalCount;
    },
    { params: { buId: buId ?? null }, staleTime: 30_000 },
  );

  // Atividades (para o bloco "Atividades de hoje")
  const activitiesQuery = useTenantQuery<ActivitiesPage>(
    "dashboard-activities",
    () => listActivities({ pageSize: 10 }),
    { params: { buId: buId ?? null }, staleTime: 30_000 },
  );

  // Inbox (para o bloco "Atualizações recentes")
  const inboxQuery = useTenantQuery<InboxItemData[]>(
    "dashboard-inbox",
    () => listInbox(),
    { params: { buId: buId ?? null }, staleTime: 30_000 },
  );

  const loading =
    reportsQuery.isLoading ||
    oppsQuery.isLoading ||
    activitiesQuery.isLoading ||
    inboxQuery.isLoading;

  const error =
    reportsQuery.isError || oppsQuery.isError
      ? "WEB-ERR-009"
      : null;

  const kpis = useMemo<DashboardKpis | null>(() => {
    if (!reportsQuery.data) return null;
    const openCount = oppsQuery.data ?? 0;
    return deriveKpisFromReports(reportsQuery.data, openCount);
  }, [reportsQuery.data, oppsQuery.data]);

  const funnel = useMemo<FunnelItem[]>(
    () => (reportsQuery.data ? mergeFunnelRows(reportsQuery.data.funnel) : []),
    [reportsQuery.data],
  );

  const ranking = reportsQuery.data?.ranking ?? [];
  const activities = activitiesQuery.data ?? null;
  const inbox = inboxQuery.data ?? [];

  function refetch() {
    void reportsQuery.refetch();
    void oppsQuery.refetch();
    void activitiesQuery.refetch();
    void inboxQuery.refetch();
  }

  return { kpis, funnel, ranking, activities, inbox, loading, error, refetch };
}

// ---------------------------------------------------------------------------
// Hook: useDashboardKpis — mantido para compatibilidade com testes existentes
// (dashboard-feature.test.tsx importa este hook diretamente)
// ---------------------------------------------------------------------------

export interface UseDashboardKpisResult {
  kpis: DashboardKpis | null;
  funnel: FunnelItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Atalho para uso em componentes que só precisam de kpis + funil.
 * Internamente delega para `useDashboardData`.
 */
export function useDashboardKpis(buId?: string | null): UseDashboardKpisResult {
  const result = useDashboardData(buId);
  return {
    kpis: result.kpis,
    funnel: result.funnel,
    loading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}

// ---------------------------------------------------------------------------
// Hook: useGoalsForecast — re-exportado de reports/hooks para evitar duplicata.
//
// A versão em reports/hooks.ts é a fonte canônica (usado por Dashboard e Reports).
// Esta re-exportação mantém compatibilidade retroativa com importações de
// 'dashboard/hooks' sem duplicar a implementação (Finding dashboard/hooks.ts:364).
// ---------------------------------------------------------------------------

export type { GoalsForecastResult as UseGoalsForecastResult } from "../reports/hooks.js";
export { useGoalsForecast } from "../reports/hooks.js";
